import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import API from '../../../api';
import BackButton from '../../../components/BackButton';
import ExcelDownloadButton from '../../../components/common/ExcelDownloadButton';
import { exportToExcel } from '../../../utils/excelExport';
import { useCurrencyFormatter, useDateFormatter, useDateTimeFormatter } from '../../../context/RegionalSettingsContext';
import { academyTodayDate } from '../../../utils/regionalFormat';
import DatePicker from '../../../components/common/DatePicker';
import {
    toRegionalDateFromISO,
    toISODateFromRegional,
} from '../../../utils/date';
import { showAlert } from '../../../utils/popup';
import {
    ChartPanel,
    CompositionDonut,
    CHART_COLORS,
} from '../../../components/charts';
import { apiErrorMessage } from '../../../utils/apiError';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function currentYear() { return academyTodayDate().getFullYear(); }
function currentMonth() { return academyTodayDate().getMonth() + 1; }

function filenameFromContentDisposition(header) {
    if (!header) return null;
    const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match) {
        try {
            return decodeURIComponent(utf8Match[1].trim());
        } catch {
            return utf8Match[1].trim();
        }
    }
    const quotedMatch = header.match(/filename="([^"]+)"/i);
    if (quotedMatch) return quotedMatch[1];
    const plainMatch = header.match(/filename=([^;]+)/i);
    if (plainMatch) return plainMatch[1].trim().replace(/^"|"$/g, '');
    return null;
}

function buildStatementPdfFilename(studentName, periodToStr, estimateToStr) {
    const safeName = (studentName || '')
        .trim()
        .replace(/[\\/:*?"<>|]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    const adv = estimateToStr || periodToStr;
    if (!safeName) return `${periodToStr}_adv_${adv}.pdf`;
    return `${safeName}_${periodToStr}_adv_${adv}.pdf`;
}

function yearRange() {
    const y = currentYear();
    const years = [];
    for (let i = y - 2; i <= y + 1; i++) years.push(i);
    return years;
}

function monthLabel(m, t) {
    return t(`reports.monthlyStatements.months.${m}`, { defaultValue: String(m) });
}

function isoDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function calendarMonthRange(year, month) {
    const from = new Date(year, month - 1, 1);
    const to = new Date(year, month, 0);
    return { from: isoDate(from), to: isoDate(to) };
}

function computeDefaultEstimatePeriod(periodToStr) {
    if (!periodToStr) return null;
    const to = new Date(periodToStr + 'T00:00:00');
    if (Number.isNaN(to.getTime())) return null;

    const y = to.getFullYear();
    const m = to.getMonth();
    const lastDayOfMonth = new Date(y, m + 1, 0).getDate();

    let upFrom;
    let upTo;
    if (to.getDate() === lastDayOfMonth) {
        upFrom = new Date(y, m + 1, 1);
        upTo = new Date(y, m + 2, 0);
    } else {
        upFrom = new Date(to);
        upFrom.setDate(upFrom.getDate() + 1);
        upTo = new Date(y, m + 1, 0);
    }

    if (upFrom > upTo) return null;

    const dayCount = Math.round((upTo - upFrom) / 86400000) + 1;
    return { from: isoDate(upFrom), to: isoDate(upTo), dayCount };
}

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_META = {
    SENT: { bg: 'bg-green-100', text: 'text-green-700', icon: '✅', labelKey: 'statusSent' },
    PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: '⏳', labelKey: 'statusPending' },
    PROCESSING: { bg: 'bg-blue-100', text: 'text-blue-700', icon: '🔄', labelKey: 'statusProcessing' },
    FAILED: { bg: 'bg-red-100', text: 'text-red-700', icon: '❌', labelKey: 'statusFailed' },
    CANCELLED: { bg: 'bg-gray-100', text: 'text-gray-600', icon: '⛔', labelKey: 'statusCancelled' },
};

function StatusBadge({ status }) {
    const { t } = useTranslation();
    const m = STATUS_META[status] ?? { bg: 'bg-gray-100', text: 'text-gray-500', icon: '—', labelKey: null };
    const label = m.labelKey
        ? t(`reports.monthlyStatements.${m.labelKey}`)
        : (status ?? '—');
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${m.bg} ${m.text}`}>
            {m.icon} {label}
        </span>
    );
}

// ─── Confirmation dialog ───────────────────────────────────────────────────────

function ConfirmDialog({ open, title, message, onConfirm, onCancel, confirmLabel, danger = false }) {
    const { t } = useTranslation();
    const resolvedConfirm = confirmLabel || t('common.confirm', { defaultValue: 'Confirm' });
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
                <h3 className="text-lg font-semibold mb-2">{title}</h3>
                <p className="text-gray-600 text-sm mb-5">{message}</p>
                <div className="flex gap-3 justify-end">
                    <button onClick={onCancel} className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">
                        {t('common.cancel')}
                    </button>
                    <button
                        onClick={onConfirm}
                        className={`px-4 py-2 text-sm rounded-lg text-white font-semibold ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                        {resolvedConfirm}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, color = 'text-gray-900', sub }) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-1 shadow-sm">
            <div className={`text-3xl font-bold ${color}`}>{value}</div>
            <div className="text-sm text-gray-500 font-medium">{label}</div>
            {sub && <div className="text-xs text-gray-400">{sub}</div>}
        </div>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function MonthlyStatementControl({ embeddedInHub = false }) {
    const { t } = useTranslation();
    const formatCurrency = useCurrencyFormatter();
    const formatDate = useDateFormatter();
    const formatDateTime = useDateTimeFormatter();

    const formatIsoRange = useCallback((fromStr, toStr) => {
        if (!fromStr || !toStr) return '—';
        return `${formatDate(fromStr + 'T00:00:00')} – ${formatDate(toStr + 'T00:00:00')}`;
    }, [formatDate]);

    const now = academyTodayDate();
    const thisMonth = calendarMonthRange(now.getFullYear(), now.getMonth() + 1);
    const [periodFrom, setPeriodFrom] = useState(thisMonth.from);
    const [periodTo, setPeriodTo] = useState(thisMonth.to);
    const [estimateFrom, setEstimateFrom] = useState('');
    const [estimateTo, setEstimateTo] = useState('');
    const [estimateAuto, setEstimateAuto] = useState(true);
    const [includeUpcomingEstimate, setIncludeUpcomingEstimate] = useState(true);

    const [periodMeta, setPeriodMeta] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [queueData, setQueueData] = useState([]);    // List<MonthlyStatementQueueItemDto>
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [loaded, setLoaded] = useState(false);

    // Per-student action loading state: { [studentId]: 'send'|'cancel'|'retry'|null }
    const [actionLoading, setActionLoading] = useState({});
    // Global "Send All" loading
    const [sendAllLoading, setSendAllLoading] = useState(false);
    // Per-row toast/flash message: { [studentId]: { type:'success'|'error', msg } }
    const [rowMessages, setRowMessages] = useState({});
    // Global message
    const [globalMsg, setGlobalMsg] = useState(null);

    // Confirm dialog state
    const [confirm, setConfirm] = useState(null); // { title, message, onConfirm, danger, confirmLabel }

    const [pdfLoading, setPdfLoading] = useState({}); // { [studentId]: true/false }
    const [studentSearch, setStudentSearch] = useState('');

    const defaultEstimatePeriod = useMemo(
        () => computeDefaultEstimatePeriod(periodTo),
        [periodTo]
    );

    useEffect(() => {
        if (!estimateAuto || !defaultEstimatePeriod) return;
        setEstimateFrom(defaultEstimatePeriod.from);
        setEstimateTo(defaultEstimatePeriod.to);
    }, [estimateAuto, defaultEstimatePeriod]);

    const statementParams = useMemo(() => ({
        from: periodFrom,
        to: periodTo,
        estimateFrom: estimateFrom || undefined,
        estimateTo: estimateTo || undefined,
        includeUpcomingEstimate,
    }), [periodFrom, periodTo, estimateFrom, estimateTo, includeUpcomingEstimate]);

    const estimateRangeValid = estimateFrom && estimateTo && estimateTo >= estimateFrom;
    const canLoad = periodFrom && periodTo && periodTo >= periodFrom
        && (!includeUpcomingEstimate || estimateRangeValid);

    // ── data loading ──────────────────────────────────────────────────

    const loadData = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        setPreviewData(null);
        setPeriodMeta(null);
        setQueueData([]);
        setLoaded(false);
        setRowMessages({});
        setGlobalMsg(null);
        setStudentSearch('');
        try {
            const [previewRes, queueRes] = await Promise.all([
                API.get('/admin/monthly-statements/preview', { params: statementParams }),
                API.get('/admin/monthly-statements/queue', { params: { from: periodFrom, to: periodTo } }),
            ]);
            setPeriodMeta(previewRes.data?.period ?? null);
            setPreviewData(previewRes.data?.students ?? previewRes.data ?? []);
            setQueueData(queueRes.data ?? []);
            setLoaded(true);
        } catch (err) {
            setLoadError(apiErrorMessage(err, t('reports.monthlyStatements.loadFailed')));
        } finally {
            setLoading(false);
        }
    }, [statementParams, periodFrom, periodTo, t]);

    const refreshQueue = useCallback(async () => {
        try {
            const res = await API.get('/admin/monthly-statements/queue', {
                params: { from: periodFrom, to: periodTo },
            });
            setQueueData(res.data ?? []);
        } catch {
            // silently ignore refresh errors
        }
    }, [periodFrom, periodTo]);

    // ── helper: set a row message; auto-clear success only (errors stay readable)
    function setRowMsg(key, type, msg) {
        setRowMessages(prev => ({ ...prev, [key]: { type, msg } }));
        if (type === 'error') return;
        setTimeout(() => setRowMessages(prev => {
            const n = { ...prev };
            if (n[key]?.type === 'success') delete n[key];
            return n;
        }), 4000);
    }

    // ── derived data: merge preview rows with queue info ──────────────

    // Build a map: studentId → latest queue item for this month/year
    const queueByStudent = {};
    for (const item of queueData) {
        const sid = item.studentId;
        if (sid == null) continue;
        // keep the most-recent entry per student (array is already sorted desc)
        if (!queueByStudent[sid]) queueByStudent[sid] = item;
    }

    const filteredPreviewData = useMemo(() => {
        if (!previewData) return [];
        const term = studentSearch.trim().toLowerCase();
        if (!term) return previewData;
        return previewData.filter(row => {
            const haystack = [
                row.studentName,
                row.email,
                row.studentId,
            ].map(v => (v ?? '').toString().toLowerCase());
            return haystack.some(v => v.includes(term));
        });
    }, [previewData, studentSearch]);

    // ── stat counters ─────────────────────────────────────────────────

    const stats = { total: 0, sent: 0, pending: 0, failed: 0, notSent: 0 };
    if (previewData) {
        stats.total = previewData.length;
        for (const row of previewData) {
            const qi = queueByStudent[row.studentId];
            const s = qi?.status;
            if (s === 'SENT') stats.sent++;
            else if (s === 'PENDING' || s === 'PROCESSING') stats.pending++;
            else if (s === 'FAILED') stats.failed++;
            else stats.notSent++;
        }
    }

    const statementStatusChartData = useMemo(() => {
        if (!loaded || !previewData) return [];
        return [
            {
                name: t('reports.monthlyStatements.sent', { defaultValue: 'Sent' }),
                value: stats.sent,
                color: CHART_COLORS.success,
            },
            {
                name: t('reports.monthlyStatements.pending', { defaultValue: 'Pending' }),
                value: stats.pending,
                color: CHART_COLORS.warning,
            },
            {
                name: t('reports.monthlyStatements.failed', { defaultValue: 'Failed' }),
                value: stats.failed,
                color: CHART_COLORS.danger,
            },
            {
                name: t('reports.monthlyStatements.notSent', { defaultValue: 'Not sent' }),
                value: stats.notSent,
                color: CHART_COLORS.slate,
            },
        ].filter((d) => d.value > 0);
    }, [loaded, previewData, stats.sent, stats.pending, stats.failed, stats.notSent, t]);

    // ── actions ───────────────────────────────────────────────────────

    function withConfirm(title, message, confirmLabel, danger, action) {
        setConfirm({ title, message, confirmLabel, danger, onConfirm: () => { setConfirm(null); action(); } });
    }

    async function handleSendAll() {
        const willSend = stats.notSent + stats.failed;
        const willSkip = stats.sent + stats.pending;
        const skipNote = willSkip > 0
            ? t('reports.monthlyStatements.skipNote', { count: willSkip })
            : '';
        withConfirm(
            t('reports.monthlyStatements.confirmSendAllTitle', { range: formatIsoRange(periodFrom, periodTo) }),
            t('reports.monthlyStatements.confirmSendAllMsg', { count: willSend, skipNote }),
            t('reports.monthlyStatements.sendAllLabel'),
            false,
            async () => {
                setSendAllLoading(true);
                setGlobalMsg(null);
                try {
                    await API.post('/admin/monthly-statements/send-all', null, { params: statementParams });
                    setGlobalMsg({ type: 'success', msg: t('reports.monthlyStatements.queuedAll') });
                    await refreshQueue();
                } catch (err) {
                    setGlobalMsg({ type: 'error', msg: apiErrorMessage(err, t('reports.monthlyStatements.sendAllFailed')) });
                } finally {
                    setSendAllLoading(false);
                }
            }
        );
    }

    async function handleSendOne(studentId, studentName) {
        withConfirm(
            t('reports.monthlyStatements.confirmSendOneTitle', { name: studentName }),
            t('reports.monthlyStatements.confirmSendOneMsg', { name: studentName, range: formatIsoRange(periodFrom, periodTo) }),
            t('reports.monthlyStatements.send'),
            false,
            async () => {
                setActionLoading(prev => ({ ...prev, [studentId]: 'send' }));
                try {
                    await API.post(`/admin/monthly-statements/send/${studentId}`, null, { params: statementParams });
                    setRowMsg(studentId, 'success', t('reports.monthlyStatements.queued'));
                    await refreshQueue();
                } catch (err) {
                    setRowMsg(studentId, 'error', apiErrorMessage(err, t('reports.monthlyStatements.queueFailed')));
                } finally {
                    setActionLoading(prev => ({ ...prev, [studentId]: null }));
                }
            }
        );
    }

    async function handleCancel(emailQueueId, studentId) {
        withConfirm(
            t('reports.monthlyStatements.cancelTitle'),
            t('reports.monthlyStatements.cancelMsg'),
            t('reports.monthlyStatements.cancelLabel'),
            true,
            async () => {
                setActionLoading(prev => ({ ...prev, [studentId]: 'cancel' }));
                try {
                    await API.post(`/admin/monthly-statements/cancel/${emailQueueId}`);
                    setRowMsg(studentId, 'success', t('reports.monthlyStatements.cancelled'));
                    await refreshQueue();
                } catch (err) {
                    setRowMsg(studentId, 'error', apiErrorMessage(err, t('reports.monthlyStatements.cancelFailed')));
                } finally {
                    setActionLoading(prev => ({ ...prev, [studentId]: null }));
                }
            }
        );
    }

    async function handleRetry(emailQueueId, studentId) {
        setActionLoading(prev => ({ ...prev, [studentId]: 'retry' }));
        try {
            await API.post(`/admin/monthly-statements/retry/${emailQueueId}`);
            setRowMsg(studentId, 'success', t('reports.monthlyStatements.retryQueued'));
            await refreshQueue();
        } catch (err) {
            setRowMsg(studentId, 'error', apiErrorMessage(err, t('reports.monthlyStatements.retryFailed')));
        } finally {
            setActionLoading(prev => ({ ...prev, [studentId]: null }));
        }
    }

    function handlePreviewPdf(studentId) {
        setPdfLoading(prev => ({ ...prev, [studentId]: 'preview' }));
        API.get(`/admin/monthly-statements/pdf/${studentId}`, {
            params: statementParams,
            responseType: 'blob',
        })
            .then((res) => {
                const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                const previewTab = window.open(blobUrl, '_blank');
                if (!previewTab) {
                    showAlert(t('reports.monthlyStatements.allowPopups'));
                }
                setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
            })
            .catch((err) => {
                const msg = err?.response?.status === 403
                    ? t('reports.monthlyStatements.accessDenied')
                    : (apiErrorMessage(err, t('reports.monthlyStatements.pdfLoadFailed')));
                showAlert(t('reports.monthlyStatements.pdfError', { message: msg }));
            })
            .finally(() => {
                setPdfLoading(prev => ({ ...prev, [studentId]: null }));
            });
    }

    function handleDownloadPdf(studentId, studentName) {
        setPdfLoading(prev => ({ ...prev, [studentId]: 'download' }));
        API.get(`/admin/monthly-statements/pdf/${studentId}`, {
            params: statementParams,
            responseType: 'blob',
        })
            .then((res) => {
                const headerFilename = filenameFromContentDisposition(res.headers?.['content-disposition']);
                const filename = headerFilename || buildStatementPdfFilename(
                    studentName,
                    statementParams.to,
                    statementParams.estimateTo,
                );
                const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                link.remove();
                setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
            })
            .catch((err) => {
                const msg = err?.response?.status === 403
                    ? t('reports.monthlyStatements.accessDenied')
                    : (apiErrorMessage(err, t('reports.monthlyStatements.pdfDownloadFailed')));
                showAlert(t('reports.monthlyStatements.pdfError', { message: msg }));
            })
            .finally(() => {
                setPdfLoading(prev => ({ ...prev, [studentId]: null }));
            });
    }

    // ── render ────────────────────────────────────────────────────────

    return (
        <div className={embeddedInHub ? "space-y-6 px-3 sm:px-6 pb-6" : "p-6 space-y-6"}>
            {!embeddedInHub && <BackButton />}

            <div className="flex items-center justify-between">
                <div>
                    {!embeddedInHub && (
                        <h2 className="page-header-title mb-2">{t('reports.monthlyStatements.title')}</h2>
                    )}
                    <p className="text-sm text-gray-600">{t('reports.monthlyStatements.subtitle')}</p>
                </div>
            </div>

            <div className="page-card space-y-5">
                <div className="flex flex-col gap-1 border-b border-slate-100 pb-4 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <p className="form-section-title mb-2">{t('reports.monthlyStatements.periodSettings')}</p>
                        <h3 className="text-lg font-semibold text-slate-900">{t('reports.monthlyStatements.statementAndEstimate')}</h3>
                        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                            {t('reports.monthlyStatements.periodDesc')}
                        </p>
                    </div>
                    {periodFrom && periodTo && periodTo >= periodFrom && (
                        <div className="rounded-2xl border border-primary-100 bg-primary-50/70 px-4 py-2 text-sm text-primary-800">
                            <span className="font-medium">{t('reports.monthlyStatements.statementLabel')}</span>{' '}
                            {formatIsoRange(periodFrom, periodTo)}
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-4 xl:gap-6 items-stretch">
                    <div className="form-section space-y-4 bg-gradient-to-br from-indigo-50/70 via-white to-white">
                        <div>
                            <h4 className="text-sm font-semibold text-slate-800">{t('reports.monthlyStatements.statementPeriod')}</h4>
                            <p className="field-note">{t('reports.monthlyStatements.statementPeriodDesc')}</p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="form-label mb-1">{t('reports.common.from')}</label>
                                <DatePicker
                                    value={toRegionalDateFromISO(periodFrom)}
                                    onChange={(v) => {
                                        const iso = toISODateFromRegional(v);
                                        if (iso) setPeriodFrom(iso);
                                    }}
                                    className="input py-2.5 w-full"
                                />
                            </div>
                            <div>
                                <label className="form-label mb-1">{t('reports.common.to')}</label>
                                <DatePicker
                                    value={toRegionalDateFromISO(periodTo)}
                                    onChange={(v) => {
                                        const iso = toISODateFromRegional(v);
                                        if (iso) setPeriodTo(iso);
                                    }}
                                    className="input py-2.5 w-full"
                                />
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => {
                                    const r = calendarMonthRange(currentYear(), currentMonth());
                                    setPeriodFrom(r.from);
                                    setPeriodTo(r.to);
                                }}
                                className="role-chip"
                            >
                                {t('reports.monthlyStatements.thisMonth')}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const d = academyTodayDate();
                                    d.setMonth(d.getMonth() - 1);
                                    const r = calendarMonthRange(d.getFullYear(), d.getMonth() + 1);
                                    setPeriodFrom(r.from);
                                    setPeriodTo(r.to);
                                }}
                                className="role-chip"
                            >
                                {t('reports.monthlyStatements.lastMonth')}
                            </button>
                        </div>
                    </div>

                    <div className="hidden xl:flex items-center justify-center px-1">
                        <div className="flex h-full min-h-[120px] flex-col items-center justify-center gap-2 text-slate-300">
                            <div className="h-full w-px bg-gradient-to-b from-transparent via-slate-200 to-transparent" />
                            <span className="rounded-full border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-400">
                                {t('reports.monthlyStatements.then')}
                            </span>
                            <div className="h-full w-px bg-gradient-to-b from-transparent via-slate-200 to-transparent" />
                        </div>
                    </div>

                    <div className="form-section space-y-4 bg-gradient-to-br from-violet-50/70 via-white to-white">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <h4 className="text-sm font-semibold text-slate-800">{t('reports.monthlyStatements.estimatePeriod')}</h4>
                                <p className="field-note">
                                    {t('reports.monthlyStatements.estimatePeriodDesc')}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setEstimateAuto(true)}
                                disabled={!defaultEstimatePeriod}
                                className="role-chip disabled:opacity-50"
                                title={t('reports.monthlyStatements.resetDefaultTitle')}
                            >
                                {t('reports.monthlyStatements.resetDefault')}
                            </button>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="form-label mb-1">{t('reports.common.from')}</label>
                                <DatePicker
                                    value={toRegionalDateFromISO(estimateFrom)}
                                    onChange={(v) => {
                                        setEstimateAuto(false);
                                        const iso = toISODateFromRegional(v);
                                        if (iso) setEstimateFrom(iso);
                                    }}
                                    className="input py-2.5 w-full"
                                />
                            </div>
                            <div>
                                <label className="form-label mb-1">{t('reports.common.to')}</label>
                                <DatePicker
                                    value={toRegionalDateFromISO(estimateTo)}
                                    onChange={(v) => {
                                        setEstimateAuto(false);
                                        const iso = toISODateFromRegional(v);
                                        if (iso) setEstimateTo(iso);
                                    }}
                                    className="input py-2.5 w-full"
                                />
                            </div>
                        </div>

                        {defaultEstimatePeriod && estimateAuto && (
                            <div className="rounded-xl border border-emerald-100 bg-emerald-50/80 px-3 py-2 text-sm text-emerald-800">
                                {t('reports.monthlyStatements.autoRange', {
                                    range: formatIsoRange(defaultEstimatePeriod.from, defaultEstimatePeriod.to),
                                    count: defaultEstimatePeriod.dayCount,
                                })}
                            </div>
                        )}
                        {!estimateAuto && estimateRangeValid && (
                            <div className="rounded-xl border border-amber-100 bg-amber-50/80 px-3 py-2 text-sm text-amber-800">
                                {t('reports.monthlyStatements.customEstimateRange')}
                            </div>
                        )}
                    </div>
                </div>

                <label className="toggle-card cursor-pointer">
                    <span className="text-sm text-slate-700">
                        <span className="font-semibold">{t('reports.monthlyStatements.includeFeeEstimateTitle')}</span>
                        <span className="block text-xs text-slate-500 mt-1">
                            {t('reports.monthlyStatements.includeFeeEstimateDesc')}
                        </span>
                    </span>
                    <input
                        type="checkbox"
                        checked={includeUpcomingEstimate}
                        onChange={e => setIncludeUpcomingEstimate(e.target.checked)}
                        className="h-5 w-5 shrink-0"
                    />
                </label>

                <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-4">
                    <button
                        onClick={loadData}
                        disabled={loading || !canLoad}
                        className="btn-primary px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
                    >
                        {loading ? t('reports.monthlyStatements.loading') : t('reports.monthlyStatements.loadPreview')}
                    </button>

                    {loaded && (
                        <button
                            onClick={loadData}
                            disabled={loading}
                            className="role-chip disabled:opacity-50"
                            title={t('reports.monthlyStatements.refreshTitle')}
                        >
                            {t('reports.monthlyStatements.refreshQueue')}
                        </button>
                    )}

                    {includeUpcomingEstimate && !estimateRangeValid && (
                        <p className="text-sm text-red-600 ml-auto">{t('reports.monthlyStatements.estimateInvalid')}</p>
                    )}
                </div>
            </div>

            {/* Load error */}
            {loadError && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">
                    ❌ {loadError}
                </div>
            )}

            {/* After data is loaded */}
            {loaded && previewData && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ChartPanel
                            title={t('charts.statementStatusMix', { defaultValue: 'Statement email status' })}
                            subtitle={t('charts.statementStatusMixHint', { defaultValue: 'Sent, pending, failed, and not sent' })}
                            loading={loading}
                            empty={!loading && statementStatusChartData.length === 0}
                            emptyMessage={t('charts.noStatementStatusData', { defaultValue: 'No statement status to chart' })}
                            error={loadError || null}
                            height={240}
                        >
                            <CompositionDonut
                                data={statementStatusChartData}
                                chartSize={180}
                                centerValue={stats.total}
                                centerLabel={t('reports.monthlyStatements.totalStudents', { defaultValue: 'Students' })}
                            />
                        </ChartPanel>
                    </div>

                    {/* Stat cards */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <StatCard label={t('reports.monthlyStatements.totalStudents')} value={stats.total} />
                        <StatCard label={t('reports.monthlyStatements.sent')} value={stats.sent} color="text-green-600" />
                        <StatCard label={t('reports.monthlyStatements.pending')} value={stats.pending} color="text-yellow-600" />
                        <StatCard label={t('reports.monthlyStatements.failed')} value={stats.failed} color="text-red-600" />
                        <StatCard label={t('reports.monthlyStatements.notSent')} value={stats.notSent} color="text-gray-500" />
                    </div>

                    {/* Student search */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                            <div className="relative flex-1 max-w-xl">
                                <input
                                    type="text"
                                    value={studentSearch}
                                    onChange={e => setStudentSearch(e.target.value)}
                                    placeholder={t('reports.monthlyStatements.searchStudents')}
                                    className="w-full px-4 py-2.5 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                {studentSearch ? (
                                    <button
                                        type="button"
                                        onClick={() => setStudentSearch('')}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        title={t('reports.monthlyStatements.clearSearch')}
                                    >
                                        ✕
                                    </button>
                                ) : (
                                    <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                )}
                            </div>
                            <div className="text-sm text-gray-500 whitespace-nowrap">
                                {t('reports.monthlyStatements.showingStudents', {
                                    filtered: filteredPreviewData.length,
                                    total: previewData.length,
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Global action bar */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex flex-wrap items-center gap-3">
                        <div className="flex-1 text-sm text-gray-600">
                            <div>
                                <span className="font-medium text-gray-800">{t('reports.monthlyStatements.statementLabel')}</span>{' '}
                                <strong>{formatIsoRange(periodFrom, periodTo)}</strong>
                            </div>
                            {includeUpcomingEstimate && (
                                <div className="mt-1">
                                    <span className="font-medium text-gray-800">{t('reports.monthlyStatements.estimateLabel')}</span>{' '}
                                    <strong>{formatIsoRange(
                                        periodMeta?.upcomingFrom ?? estimateFrom,
                                        periodMeta?.upcomingTo ?? estimateTo
                                    )}</strong>
                                </div>
                            )}
                            <div className="mt-1">
                                {t('reports.monthlyStatements.studentsWithActivity', {
                                    count: previewData.length,
                                })}
                            </div>
                        </div>

                        <button
                            onClick={handleSendAll}
                            disabled={sendAllLoading || previewData.length === 0}
                            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white text-sm font-semibold rounded-lg transition"
                        >
                            {sendAllLoading ? t('reports.monthlyStatements.queueing') : t('reports.monthlyStatements.sendAllWithCount', { count: stats.notSent + stats.failed })}
                        </button>

                        <ExcelDownloadButton
                            onExport={() => {
                                const rows = (filteredPreviewData || []).map(row => {
                                    const qi = queueByStudent[row.studentId];
                                    return {
                                        studentName: row.studentName,
                                        email: row.email ?? '',
                                        sessionCount: row.sessionCount,
                                        totalCharges: formatCurrency(row.totalCharges || 0),
                                        closingBalance: formatCurrency(row.closingBalance || 0),
                                        nextMonthEstimate: formatCurrency(row.nextMonthEstimate || 0),
                                        amountToDeposit: formatCurrency(row.amountToDeposit || 0),
                                        paymentRequired: row.paymentRequired
                                            ? t('common.yes', { defaultValue: 'Yes' })
                                            : t('common.no', { defaultValue: 'No' }),
                                        emailStatus: qi?.status
                                            ? t(`reports.monthlyStatements.status${String(qi.status).charAt(0)}${String(qi.status).slice(1).toLowerCase()}`, {
                                                defaultValue: String(qi.status).replace(/_/g, ' '),
                                            })
                                            : t('reports.monthlyStatements.notSentStatus'),
                                    };
                                });
                                exportToExcel([
                                    { header: t('reports.monthlyStatements.studentCol'), key: 'studentName', width: 24 },
                                    { header: t('reports.monthlyStatements.emailCol'), key: 'email', width: 28 },
                                    { header: t('reports.common.sessions'), key: 'sessionCount', width: 10 },
                                    { header: t('reports.monthlyStatements.charges'), key: 'totalCharges', width: 12 },
                                    { header: t('reports.monthlyStatements.closingBalance'), key: 'closingBalance', width: 16 },
                                    { header: t('reports.monthlyStatements.estimateFees'), key: 'nextMonthEstimate', width: 16 },
                                    { header: t('reports.monthlyStatements.depositDue'), key: 'amountToDeposit', width: 14 },
                                    { header: t('reports.monthlyStatements.paymentReminder'), key: 'paymentRequired', width: 16 },
                                    { header: t('reports.monthlyStatements.emailStatus'), key: 'emailStatus', width: 14 },
                                ], rows, `statements-${periodFrom}_${periodTo}`);
                            }}
                            filename={`statements-${periodFrom}_${periodTo}`}
                            disabled={!filteredPreviewData || filteredPreviewData.length === 0}
                        />

                        {globalMsg && (
                            <span className={`text-sm font-medium ${globalMsg.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                                {globalMsg.msg}
                            </span>
                        )}
                    </div>

                    {/* Student table */}
                    {previewData.length === 0 ? (
                        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-500 shadow-sm">
                            {t('reports.monthlyStatements.noStudentsActivity', { range: formatIsoRange(periodFrom, periodTo) })}
                        </div>
                    ) : filteredPreviewData.length === 0 ? (
                        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-500 shadow-sm">
                            {t('reports.monthlyStatements.noStudentsMatch', { term: studentSearch })}
                        </div>
                    ) : (
                        <>
                            {/* Mobile: student preview cards */}
                            <div className="md:hidden space-y-3">
                                {filteredPreviewData.map(row => {
                                    const qi = queueByStudent[row.studentId];
                                    const status = qi?.status ?? null;
                                    const busy = actionLoading[row.studentId];
                                    const rowMsg = rowMessages[row.studentId];
                                    const estimatePrepaid = !includeUpcomingEstimate
                                        || (row.paymentRequired === false && row.nextMonthEstimate > 0);

                                    return (
                                        <article
                                            key={row.studentId}
                                            className="rounded-xl border border-gray-200 bg-white p-3.5 shadow-sm"
                                        >
                                            <div className="flex items-start justify-between gap-2 mb-3">
                                                <div className="min-w-0">
                                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                                        {t('reports.monthlyStatements.studentCol')}
                                                    </p>
                                                    <p className="text-base font-semibold text-gray-900 truncate">
                                                        {row.studentName}
                                                    </p>
                                                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                                                        {row.email ?? '—'}
                                                    </p>
                                                    {row.studentActive === false && (
                                                        <span className="mt-1 inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                                                            {t('reports.monthlyStatements.archived')}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                    {status ? <StatusBadge status={status} /> : (
                                                        <span className="text-gray-400 text-xs">{t('reports.monthlyStatements.notSentStatus')}</span>
                                                    )}
                                                    {status === 'SENT' && qi.sentAt && (
                                                        <span className="text-xs text-gray-400">{formatDateTime(qi.sentAt)}</span>
                                                    )}
                                                    {status === 'FAILED' && qi.failedAt && (
                                                        <span className="text-xs text-red-400" title={qi.errorMessage ?? ''}>
                                                            {formatDateTime(qi.failedAt)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm border-t border-gray-100 pt-3">
                                                <div className="min-w-0">
                                                    <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t('reports.common.sessions')}</dt>
                                                    <dd className="font-medium text-gray-800 tabular-nums">{row.sessionCount}</dd>
                                                </div>
                                                <div className="min-w-0">
                                                    <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t('reports.monthlyStatements.charges')}</dt>
                                                    <dd className="font-medium text-gray-800 tabular-nums">{formatCurrency(row.totalCharges)}</dd>
                                                </div>
                                                <div className="min-w-0">
                                                    <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t('reports.monthlyStatements.closingBalance')}</dt>
                                                    <dd className={`font-semibold tabular-nums ${row.closingBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                        {row.closingBalance >= 0
                                                            ? t('reports.monthlyStatements.balanceCredit', { amount: formatCurrency(row.closingBalance) })
                                                            : t('reports.monthlyStatements.balanceDue', { amount: formatCurrency(Math.abs(row.closingBalance)) })}
                                                    </dd>
                                                </div>
                                                <div className="min-w-0">
                                                    <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t('reports.monthlyStatements.upcomingEst')}</dt>
                                                    <dd className="font-medium text-gray-800">
                                                        {estimatePrepaid ? (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                                                {!includeUpcomingEstimate ? t('reports.monthlyStatements.reportOnly') : t('reports.monthlyStatements.prepaid')}
                                                            </span>
                                                        ) : (
                                                            <span className="text-indigo-600 tabular-nums">
                                                                {formatCurrency(row.nextMonthEstimate)}
                                                                <span className="text-gray-400 text-xs ml-1">
                                                                    ({t('reports.monthlyStatements.sessionCountShort', { count: row.nextMonthSessionCount })})
                                                                </span>
                                                            </span>
                                                        )}
                                                        {includeUpcomingEstimate && estimatePrepaid && (
                                                            <div className="text-gray-400 text-xs mt-1">
                                                                {t('reports.monthlyStatements.estimateHidden', { amount: formatCurrency(row.nextMonthEstimate) })}
                                                            </div>
                                                        )}
                                                        {!estimatePrepaid && row.paymentRequired && row.amountToDeposit > 0 && (
                                                            <div className="text-amber-700 text-xs mt-1 font-semibold">
                                                                {t('reports.monthlyStatements.amountDue', { amount: formatCurrency(row.amountToDeposit) })}
                                                            </div>
                                                        )}
                                                    </dd>
                                                </div>
                                            </dl>

                                            <div className="mt-3 pt-3 border-t border-gray-100 flex flex-col gap-1.5">
                                                {rowMsg && (
                                                    <span className={`text-xs font-semibold ${rowMsg.type === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                                                        {rowMsg.msg}
                                                    </span>
                                                )}
                                                <div className="flex flex-wrap gap-1">
                                                    <button
                                                        onClick={() => handlePreviewPdf(row.studentId)}
                                                        disabled={!!pdfLoading[row.studentId]}
                                                        title={t('reports.monthlyStatements.previewPdf')}
                                                        className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 text-gray-600 disabled:opacity-50 transition"
                                                    >
                                                        {pdfLoading[row.studentId] === 'preview' ? '⏳' : '📄'}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDownloadPdf(row.studentId, row.studentName)}
                                                        disabled={!!pdfLoading[row.studentId]}
                                                        title={t('reports.monthlyStatements.downloadPdf')}
                                                        className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 text-gray-600 disabled:opacity-50 transition"
                                                    >
                                                        {pdfLoading[row.studentId] === 'download' ? '⏳' : '⬇'}
                                                    </button>
                                                    {(!status || status === 'CANCELLED') && (
                                                        <button
                                                            onClick={() => handleSendOne(row.studentId, row.studentName)}
                                                            disabled={!!busy || !row.email}
                                                            title={!row.email ? t('reports.monthlyStatements.noEmailOnFile') : t('reports.monthlyStatements.sendStatementEmail')}
                                                            className="px-2 py-1 text-xs rounded border border-indigo-300 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 disabled:opacity-50 transition"
                                                        >
                                                            {busy === 'send' ? '…' : `📧 ${t('reports.monthlyStatements.send')}`}
                                                        </button>
                                                    )}
                                                    {status === 'SENT' && (
                                                        <button
                                                            onClick={() => handleSendOne(row.studentId, row.studentName)}
                                                            disabled={!!busy || !row.email}
                                                            title={!row.email ? t('reports.monthlyStatements.noEmailOnFile') : t('reports.monthlyStatements.resendTooltip')}
                                                            className="px-2 py-1 text-xs rounded border border-yellow-300 bg-yellow-50 hover:bg-yellow-100 text-yellow-700 disabled:opacity-50 transition"
                                                        >
                                                            {busy === 'send' ? '…' : `🔁 ${t('reports.monthlyStatements.resend')}`}
                                                        </button>
                                                    )}
                                                    {status === 'PENDING' && qi && (
                                                        <button
                                                            onClick={() => handleCancel(qi.id, row.studentId)}
                                                            disabled={!!busy}
                                                            title={t('reports.monthlyStatements.cancelEmail')}
                                                            className="px-2 py-1 text-xs rounded border border-red-200 bg-red-50 hover:bg-red-100 text-red-600 disabled:opacity-50 transition"
                                                        >
                                                            {busy === 'cancel' ? '…' : `✕ ${t('reports.monthlyStatements.cancelEmail')}`}
                                                        </button>
                                                    )}
                                                    {status === 'FAILED' && qi && (
                                                        <button
                                                            onClick={() => handleRetry(qi.id, row.studentId)}
                                                            disabled={!!busy}
                                                            title={t('reports.monthlyStatements.retry')}
                                                            className="px-2 py-1 text-xs rounded border border-orange-300 bg-orange-50 hover:bg-orange-100 text-orange-700 disabled:opacity-50 transition"
                                                        >
                                                            {busy === 'retry' ? '…' : `↩ ${t('reports.monthlyStatements.retry')}`}
                                                        </button>
                                                    )}
                                                </div>
                                                {status === 'FAILED' && qi?.errorMessage && (
                                                    <span className="text-xs text-red-400 truncate" title={qi.errorMessage}>
                                                        {qi.errorMessage}
                                                    </span>
                                                )}
                                                {status === 'FAILED' && qi.retryCount != null && (
                                                    <span className="text-xs text-gray-400">
                                                        Attempt {qi.retryCount}/{qi.maxRetries}
                                                    </span>
                                                )}
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>

                            {/* Desktop: table */}
                            <div className="hidden md:block bg-white border border-gray-200 rounded-xl shadow-sm overflow-x-auto responsive-table-scroll">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-200">
                                            <th className="px-4 py-3 text-left font-semibold text-gray-600">{t('reports.monthlyStatements.studentCol')}</th>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-600">{t('reports.monthlyStatements.emailCol')}</th>
                                            <th className="px-4 py-3 text-right font-semibold text-gray-600">{t('reports.common.sessions')}</th>
                                            <th className="px-4 py-3 text-right font-semibold text-gray-600">{t('reports.monthlyStatements.charges')}</th>
                                            <th className="px-4 py-3 text-right font-semibold text-gray-600">{t('reports.monthlyStatements.closingBalance')}</th>
                                            <th className="px-4 py-3 text-right font-semibold text-gray-600">{t('reports.monthlyStatements.upcomingEst')}</th>
                                            <th className="px-4 py-3 text-center font-semibold text-gray-600">{t('reports.monthlyStatements.emailStatus')}</th>
                                            <th className="px-4 py-3 text-center font-semibold text-gray-600">{t('reports.monthlyStatements.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredPreviewData.map(row => {
                                            const qi = queueByStudent[row.studentId];
                                            const status = qi?.status ?? null;
                                            const busy = actionLoading[row.studentId];
                                            const rowMsg = rowMessages[row.studentId];

                                            return (
                                                <tr key={row.studentId} className="border-b border-gray-100 hover:bg-gray-50 transition">
                                                    <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">
                                                        <div className="flex items-center gap-2">
                                                            <span>{row.studentName}</span>
                                                            {row.studentActive === false && (
                                                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                                                                    {t('reports.monthlyStatements.archived')}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                                                        {row.email ?? '—'}
                                                    </td>
                                                    <td className="px-4 py-3 text-right tabular-nums">
                                                        {row.sessionCount}
                                                    </td>
                                                    <td className="px-4 py-3 text-right tabular-nums">
                                                        {formatCurrency(row.totalCharges)}
                                                    </td>
                                                    <td className={`px-4 py-3 text-right tabular-nums font-semibold ${row.closingBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                        {row.closingBalance >= 0
                                                            ? t('reports.monthlyStatements.balanceCredit', { amount: formatCurrency(row.closingBalance) })
                                                            : t('reports.monthlyStatements.balanceDue', { amount: formatCurrency(Math.abs(row.closingBalance)) })}
                                                    </td>
                                                    <td className="px-4 py-3 text-right tabular-nums">
                                                        {(!includeUpcomingEstimate || (row.paymentRequired === false && row.nextMonthEstimate > 0)) ? (
                                                            <div>
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                                                    {!includeUpcomingEstimate ? t('reports.monthlyStatements.reportOnly') : t('reports.monthlyStatements.prepaid')}
                                                                </span>
                                                                {includeUpcomingEstimate && (
                                                                    <div className="text-gray-400 text-xs mt-1">
                                                                        {t('reports.monthlyStatements.estimateHiddenEmail', { amount: formatCurrency(row.nextMonthEstimate) })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="text-indigo-600 font-medium">
                                                                {formatCurrency(row.nextMonthEstimate)}
                                                                <span className="text-gray-400 text-xs ml-1">
                                                                    ({t('reports.monthlyStatements.sessionCountShort', { count: row.nextMonthSessionCount })})
                                                                </span>
                                                                {row.paymentRequired && row.amountToDeposit > 0 && (
                                                                    <div className="text-amber-700 text-xs mt-1 font-semibold">
                                                                        {t('reports.monthlyStatements.amountDue', { amount: formatCurrency(row.amountToDeposit) })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <div className="flex flex-col items-center gap-0.5">
                                                            {status ? (
                                                                <>
                                                                    <StatusBadge status={status} />
                                                                    {status === 'SENT' && qi.sentAt && (
                                                                        <span className="text-xs text-gray-400">{formatDateTime(qi.sentAt)}</span>
                                                                    )}
                                                                    {status === 'FAILED' && qi.failedAt && (
                                                                        <span className="text-xs text-red-400" title={qi.errorMessage ?? ''}>
                                                                            {formatDateTime(qi.failedAt)}
                                                                        </span>
                                                                    )}
                                                                    {status === 'FAILED' && qi.retryCount != null && (
                                                                        <span className="text-xs text-gray-400">
                                                                            Attempt {qi.retryCount}/{qi.maxRetries}
                                                                        </span>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <span className="text-gray-400 text-xs">{t('reports.monthlyStatements.notSentStatus')}</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex flex-col items-center gap-1.5">
                                                            {rowMsg && (
                                                                <span className={`text-xs font-semibold ${rowMsg.type === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                                                                    {rowMsg.msg}
                                                                </span>
                                                            )}
                                                            <div className="flex flex-wrap justify-center gap-1">
                                                                <button
                                                                    onClick={() => handlePreviewPdf(row.studentId)}
                                                                    disabled={!!pdfLoading[row.studentId]}
                                                                    title={t('reports.monthlyStatements.previewPdf')}
                                                                    className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 text-gray-600 disabled:opacity-50 transition"
                                                                >
                                                                    {pdfLoading[row.studentId] === 'preview' ? '⏳' : '📄'}
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDownloadPdf(row.studentId, row.studentName)}
                                                                    disabled={!!pdfLoading[row.studentId]}
                                                                    title={t('reports.monthlyStatements.downloadPdf')}
                                                                    className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 text-gray-600 disabled:opacity-50 transition"
                                                                >
                                                                    {pdfLoading[row.studentId] === 'download' ? '⏳' : '⬇'}
                                                                </button>
                                                                {(!status || status === 'CANCELLED') && (
                                                                    <button
                                                                        onClick={() => handleSendOne(row.studentId, row.studentName)}
                                                                        disabled={!!busy || !row.email}
                                                                        title={!row.email ? t('reports.monthlyStatements.noEmailOnFile') : t('reports.monthlyStatements.sendStatementEmail')}
                                                                        className="px-2 py-1 text-xs rounded border border-indigo-300 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 disabled:opacity-50 transition"
                                                                    >
                                                                        {busy === 'send' ? '…' : `📧 ${t('reports.monthlyStatements.send')}`}
                                                                    </button>
                                                                )}
                                                                {status === 'SENT' && (
                                                                    <button
                                                                        onClick={() => handleSendOne(row.studentId, row.studentName)}
                                                                        disabled={!!busy || !row.email}
                                                                        title={!row.email ? t('reports.monthlyStatements.noEmailOnFile') : t('reports.monthlyStatements.resendTooltip')}
                                                                        className="px-2 py-1 text-xs rounded border border-yellow-300 bg-yellow-50 hover:bg-yellow-100 text-yellow-700 disabled:opacity-50 transition"
                                                                    >
                                                                        {busy === 'send' ? '…' : `🔁 ${t('reports.monthlyStatements.resend')}`}
                                                                    </button>
                                                                )}
                                                                {status === 'PENDING' && qi && (
                                                                    <button
                                                                        onClick={() => handleCancel(qi.id, row.studentId)}
                                                                        disabled={!!busy}
                                                                        title={t('reports.monthlyStatements.cancelEmail')}
                                                                        className="px-2 py-1 text-xs rounded border border-red-200 bg-red-50 hover:bg-red-100 text-red-600 disabled:opacity-50 transition"
                                                                    >
                                                                        {busy === 'cancel' ? '…' : `✕ ${t('reports.monthlyStatements.cancelEmail')}`}
                                                                    </button>
                                                                )}
                                                                {status === 'FAILED' && qi && (
                                                                    <button
                                                                        onClick={() => handleRetry(qi.id, row.studentId)}
                                                                        disabled={!!busy}
                                                                        title={t('reports.monthlyStatements.retry')}
                                                                        className="px-2 py-1 text-xs rounded border border-orange-300 bg-orange-50 hover:bg-orange-100 text-orange-700 disabled:opacity-50 transition"
                                                                    >
                                                                        {busy === 'retry' ? '…' : `↩ ${t('reports.monthlyStatements.retry')}`}
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {status === 'FAILED' && qi?.errorMessage && (
                                                                <span className="text-xs text-red-400 max-w-[160px] truncate" title={qi.errorMessage}>
                                                                    {qi.errorMessage}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </>
            )}

            {/* Confirm dialog */}
            {confirm && (
                <ConfirmDialog
                    open={true}
                    title={confirm.title}
                    message={confirm.message}
                    confirmLabel={confirm.confirmLabel}
                    danger={confirm.danger}
                    onConfirm={confirm.onConfirm}
                    onCancel={() => setConfirm(null)}
                />
            )}
        </div>
    );
}
